import React, { useState, useEffect , useRef } from 'react';
import SuspendedBanner from './SuspendedBanner';
import { openCookieSettings } from './CookieConsent';
import { formatRelative } from '../utils/dateFormat';
import { Layout, Menu, Button, Space, Dropdown, Avatar, Badge, Empty, Drawer , notification as antNotification } from 'antd';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { UserOutlined, PlusOutlined, LogoutOutlined, GlobalOutlined, FolderOutlined, TeamOutlined, ProjectOutlined, BellFilled, BellOutlined, CheckOutlined, MessageFilled, MessageOutlined, MailOutlined, FlagOutlined, HeartOutlined, DashboardOutlined, GiftOutlined, MenuOutlined, HomeOutlined, AppstoreOutlined, SafetyOutlined, FileProtectOutlined, LockOutlined, RightOutlined, CustomerServiceOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useAuth } from '../AuthContext';
import BrandLogo from './BrandLogo';
import API from '../api';
import { playNotifySound, startRing, stopRing } from '../utils/notifySound';


const { Header, Content } = Layout;

const t = {
  ar: {
    home: 'الرئيسية', gigs: 'الخدمات', freelancers: 'المستقلين', orders: 'طلباتي',
    myGigs: 'خدماتي', createGig: 'أضف خدمة', login: 'دخول', register: 'انضم الآن',
    profile: 'حسابي', dashboard: 'لوحة المستقل', favorites: 'مفضلتي', refer: 'ادعُ أصدقاءك', savedSearches: 'بحثي المحفوظ', logout: 'تسجيل خروج',
    addProject: 'أضف مشروع', myProjects: 'مشاريعي', projects: 'المشاريع',
    findFreelancers: 'ابحث عن مستقلين', browseProjects: 'تصفح المشاريع',
    notifications: 'الإشعارات', markAllRead: 'قراءة الكل', noNotifications: 'لا توجد إشعارات',
    moderation: 'إدارة المحتوى',
    copyright: '© 2026 لانسر سيتي. جميع الحقوق محفوظة',
    footerPlatform: 'لانسر سيتي',
    footerExplore: 'اكتشف',
    footerLegal: 'الحماية والثقة',
    footerTagline: 'منصة العمل الحر الأولى للمستقلين العرب — اربط مشروعك بأفضل الكفاءات',
    fRights: 'ضمان حقوقك',
    fAbout: 'عن لانسر سيتي', fTerms: 'شروط الاستخدام',
    fPrivacy: 'بيان الخصوصية',
    fBrowseProjects: 'تصفح المشاريع',
    fFindFreelancers: 'ابحث عن مستقلين',
    fBrowseGigs: 'تصفح الخدمات',
    fAddProject: 'انشر مشروعًا',
    fAbout: 'عن المنصة',
    drawerCategories: 'التصنيفات',
    drawerAllCategories: 'عرض كل التصنيفات',
  },
  en: {
    home: 'Home', gigs: 'Services', freelancers: 'Freelancers', orders: 'My Orders',
    myGigs: 'My Gigs', createGig: 'Create Gig', login: 'Sign In', register: 'Join Now',
    profile: 'My Profile', dashboard: 'Freelancer Dashboard', favorites: 'My Favorites', refer: 'Invite friends', savedSearches: 'Saved Searches', logout: 'Sign Out',
    addProject: 'Post Project', myProjects: 'My Projects', projects: 'Projects',
    findFreelancers: 'Find Freelancers', browseProjects: 'Browse Projects',
    notifications: 'Notifications', markAllRead: 'Mark all read', noNotifications: 'No notifications',
    moderation: 'Moderation',
    copyright: '© 2026 LancerCity. All rights reserved',
    footerPlatform: 'LancerCity',
    footerExplore: 'Explore',
    footerLegal: 'Trust & Safety',
    footerTagline: 'The leading freelance platform for Arab talent — connect your project with the best',
    fRights: 'Your Rights Guarantee',
    fAbout: 'About', fTerms: 'Terms of Use',
    fPrivacy: 'Privacy Policy',
    fBrowseProjects: 'Browse Projects',
    fFindFreelancers: 'Find Freelancers',
    fBrowseGigs: 'Browse Services',
    fAddProject: 'Post a Project',
    fAbout: 'About',
    drawerCategories: 'Categories',
    drawerAllCategories: 'View all categories',
  },
};

const AppLayout = ({ children, lang, toggleLang }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const tx = t[lang] || t.ar;
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const lastSeenId = useRef(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const closeDrawer = () => setDrawerOpen(false);
  const goAndClose = (path) => { navigate(path); closeDrawer(); };

  const fetchNotifications = () => {
    if (!user) return;
    API.get('/notifications/').then(res => {
      const list = res.data.results || [];
      const unread = res.data.unread_count || 0;
      // ding when a new unread notification appears (not on first load)
      const newestId = list[0]?.id || 0;
      if (lastSeenId.current !== null && newestId > lastSeenId.current && unread > 0) {
        const n = list[0];
        if (n && !n.is_read && typeof n.title === 'string' && n.title.startsWith('📹') && !window.location.pathname.startsWith('/chat')) {
          startRing(30000);
          antNotification.open({
            key: 'lc-call', duration: 30, placement: 'topLeft',
            message: n.title,
            description: lang === 'ar' ? 'اضغط للانضمام إلى المكالمة' : 'Click to join the call',
            onClick: () => { stopRing(); antNotification.destroy('lc-call'); navigate(n.link || '/chat'); },
            onClose: () => stopRing(),
          });
        } else {
          playNotifySound();
        }
      }
      lastSeenId.current = newestId;
      setNotifications(list);
      setUnreadCount(unread);
    }).catch(() => {});
  };

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 10000);
    return () => clearInterval(interval);
  }, [user]);

const markAllRead = () => {
    API.post('/notifications/read-all/').then(() => {
      setUnreadCount(0);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    });
  };

  const handleNotificationClick = (notif) => {
    if (!notif.is_read) {
      API.post(`/notifications/${notif.id}/read/`).then(() => {
        setUnreadCount(prev => Math.max(0, prev - 1));
        setNotifications(prev => prev.map(n => n.id === notif.id ? { ...n, is_read: true } : n));
      });
    }
    if (notif.link) navigate(notif.link);
  };

  const notifMenu = {
    items: [
      {
        key: 'header',
        label: (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0' }}>
            <span style={{ fontWeight: 800, fontSize: 15, color: 'var(--primary)' }}>{tx.notifications}</span>
            {unreadCount > 0 && (
              <span onClick={markAllRead} style={{ fontSize: 12, color: 'var(--accent-mid)', cursor: 'pointer', fontWeight: 600 }}>
                <CheckOutlined /> {tx.markAllRead}
              </span>
            )}
          </div>
        ),
        disabled: true,
      },
      { type: 'divider' },
      ...(notifications.length === 0
        ? [{ key: 'empty', label: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={tx.noNotifications} />, disabled: true }]
        : notifications.slice(0, 8).map(notif => ({
            key: notif.id,
            label: (
              <div onClick={() => handleNotificationClick(notif)} style={{
                maxWidth: 300,
                padding: '6px 10px',
                borderInlineStart: notif.is_read ? '3px solid transparent' : '3px solid var(--accent-mid)',
                cursor: 'pointer',
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                  {!notif.is_read && (
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent-mid)', flexShrink: 0 }} />
                  )}
                  <div style={{ fontWeight: notif.is_read ? 400 : 700, fontSize: 13, color: 'var(--primary)' }}>
                    {notif.title}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: 'var(--text-light)', lineHeight: 1.5 }}>
                  {notif.message.substring(0, 80)}{notif.message.length > 80 ? '...' : ''}
                </div>
                <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                  {formatRelative(notif.created_at, lang)}
                </div>
              </div>
            ),
            style: { background: notif.is_read ? 'transparent' : 'rgba(46,134,193,0.10)' },
          }))
      ),
    ],
  };

  const userMenuItems = user ? [
    { key: 'profile', label: tx.profile, icon: <UserOutlined />, onClick: () => navigate('/profile') },
    { key: 'orders', label: tx.orders, icon: <FolderOutlined />, onClick: () => navigate('/orders') },
    ...(user.role === 'freelancer' ? [{ key: 'my-gigs', label: tx.myGigs, icon: <AppstoreOutlined />, onClick: () => navigate('/my-gigs') }] : []),
    ...(user.role === 'freelancer' ? [{ key: 'create', label: tx.createGig, icon: <span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:"50%",background:"#F39C12",color:"white",marginInlineEnd:6,boxShadow:"0 2px 4px rgba(243,156,18,.3)"}}><PlusOutlined style={{fontSize:13,fontWeight:900}} /></span>, onClick: () => navigate('/gigs/create') }] : []),
    ...(user.is_staff || user.is_superuser ? [{ key: 'dashboard', label: lang === 'ar' ? 'لوحة التحكم' : 'Dashboard', icon: <ProjectOutlined />, onClick: () => navigate('/dashboard') }] : []),
    ...(user.is_staff || user.is_superuser ? [{ key: 'moderation', label: tx.moderation, icon: <FlagOutlined />, onClick: () => navigate('/moderation') }] : []),
    { type: 'divider' },
    { key: 'logout', label: tx.logout, icon: <LogoutOutlined />, danger: true, onClick: logout },
  ] : [];

  return (
    <div className="app-gradient" style={{ minHeight: '100vh', position: 'relative', overflow: 'hidden' }}>
      <div className="blob blob--teal blob--tl" aria-hidden />
      <div className="blob blob--amber blob--tr" aria-hidden />
      <div className="blob blob--navy blob--br" aria-hidden />
      <Layout style={{ minHeight: '100vh', background: 'transparent', position: 'relative', zIndex: 1 }}>
      <Header className="site-header">
        {/* Left section in DOM order; in RTL flex-start, first child is rightmost.
            Hamburger placed BEFORE the logo so it appears at the far right edge. */}
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 12, minWidth: 0 }}>
          <button
            className="hamburger-btn"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
            type="button"
          >
            <MenuOutlined />
          </button>
          <Link to="/" style={{ textDecoration: 'none', lineHeight: 1.1 }}>
            <BrandLogo lang={lang} markHeight={42} />
          </Link>
        </div>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'flex-end', minWidth: 0 }}>
        <Space size={12}>
          <span className="lang-btn" onClick={toggleLang}><GlobalOutlined /> {lang === 'ar' ? 'EN' : 'عربي'}</span>
          {user ? (
            <>
              <Link to="/chat" title={lang === 'ar' ? 'الرسائل' : 'Messages'}>
                <MailOutlined className="nav-icon nav-icon--mail" />
              </Link>
              <Dropdown
                menu={notifMenu}
                placement="bottomLeft"
                trigger={['click']}
                overlayStyle={{ width: 360 }}
              >
                <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                  <BellOutlined className="nav-icon" />
                </Badge>
              </Dropdown>
              <Dropdown menu={{ items: userMenuItems }} placement="bottomLeft">
                <Space style={{ cursor: 'pointer', background: 'var(--bg)', padding: '6px 16px 6px 8px', borderRadius: 50 }}>
                  <Avatar src={user.avatar} icon={<UserOutlined />} size={40} className="nav-avatar" style={{ background: 'var(--secondary)' }} />
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{user.first_name || user.username}</span>
                </Space>
              </Dropdown>
            </>
          ) : (
            <Space size={8}>
              <Button type="text" onClick={() => navigate('/login')} style={{ fontWeight: 700, color: 'var(--text)' }}>{tx.login}</Button>
              <Button type="primary" onClick={() => navigate('/register')} style={{ borderRadius: 50, height: 40, paddingInline: 28, fontWeight: 700, fontSize: 15 }}>{tx.register}</Button>
            </Space>
          )}
        </Space>
        </div>
      </Header>

      <div className="action-bar">
        <Link to="/projects/create"><span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:"50%",background:"#F39C12",color:"white",marginInlineEnd:6,boxShadow:"0 2px 4px rgba(243,156,18,.3)"}}><PlusOutlined style={{fontSize:13,fontWeight:900}} /></span> {tx.addProject}</Link>
        <Link to="/my-projects"><FolderOutlined /> {tx.myProjects}</Link>
        <Link to="/freelancers"><TeamOutlined /> {tx.findFreelancers}</Link>
        <Link to="/projects"><ProjectOutlined /> {tx.browseProjects}</Link>
        <Link to="/gigs"><AppstoreOutlined /> {tx.gigs}</Link>
      </div>

      <Content>{children}</Content>

      <footer className="site-footer">
        <div className="site-footer-grid">
          <div className="site-footer-col site-footer-brand">
            <BrandLogo lang={lang} markHeight={36} inverted />
            <p className="site-footer-tagline">{tx.footerTagline}</p>
          </div>

          <div className="site-footer-col">
            <h4>{tx.footerExplore}</h4>
            <Link to="/projects">{tx.fBrowseProjects}</Link>
            <Link to="/freelancers">{tx.fFindFreelancers}</Link>
            <Link to="/gigs">{tx.fBrowseGigs}</Link>
            <Link to="/projects/create">{tx.fAddProject}</Link>
            <Link to="/help">{lang === 'ar' ? 'مركز المساعدة' : 'Help center'}</Link>
            <Link to="/support">{lang === 'ar' ? 'الدعم الفني' : 'Support'}</Link>
          </div>

          <div className="site-footer-col">
            <h4>{tx.footerLegal}</h4>
            <Link to="/about">{tx.fAbout}</Link>
            <Link to="/legal/rights">{tx.fRights}</Link>
            <Link to="/legal/terms">{tx.fTerms}</Link>
            <Link to="/legal/privacy">{tx.fPrivacy}</Link>
            <Link to="/legal/refund">{lang === 'ar' ? 'الدفع والاسترداد' : 'Payment & Refunds'}</Link>
            <Link to="/legal/aup">{lang === 'ar' ? 'الاستخدام المقبول' : 'Acceptable Use'}</Link>
            <a href="#cookies" onClick={(e) => { e.preventDefault(); openCookieSettings(); }}>{lang === 'ar' ? 'إعدادات الكوكيز' : 'Cookie settings'}</a>
          </div>
        </div>
        <div className="site-footer-bottom"><div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}><span>{tx.copyright}</span></div></div>
      </footer>

      <Drawer
        placement={lang === 'ar' ? 'right' : 'left'}
        open={drawerOpen}
        onClose={closeDrawer}
        width={320}
        closable={false}
        styles={{ body: { padding: 0 } }}
        className="lc-drawer"
      >
        <div className="drawer-header">
          <BrandLogo lang={lang} markHeight={32} inverted />
          <button onClick={closeDrawer} className="drawer-close" aria-label="Close menu">×</button>
        </div>
        <ul className="drawer-menu">
          <li onClick={() => goAndClose('/')}><HomeOutlined /> <span>{tx.home}</span></li>
          <li onClick={() => goAndClose('/projects/create')}><span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:"50%",background:"#F39C12",color:"white",marginInlineEnd:6,boxShadow:"0 2px 4px rgba(243,156,18,.3)"}}><PlusOutlined style={{fontSize:13,fontWeight:900}} /></span> <span>{tx.addProject}</span></li>
          {user && <li onClick={() => goAndClose('/my-projects')}><FolderOutlined /> <span>{tx.myProjects}</span></li>}
          <li onClick={() => goAndClose('/projects')}><ProjectOutlined /> <span>{tx.fBrowseProjects}</span></li>
          <li onClick={() => goAndClose('/freelancers')}><TeamOutlined /> <span>{tx.fFindFreelancers}</span></li>
          <li onClick={() => goAndClose('/gigs')}><AppstoreOutlined /> <span>{tx.fBrowseGigs}</span></li>
          {user && <li onClick={() => goAndClose('/chat')}><MailOutlined /> <span>{lang === 'ar' ? 'الرسائل' : 'Messages'}</span></li>}
          <li onClick={() => goAndClose('/support')}><CustomerServiceOutlined /> <span>{lang === 'ar' ? 'الدعم الفني' : 'Support'}</span></li>
          <li onClick={() => goAndClose('/help')}><QuestionCircleOutlined /> <span>{lang === 'ar' ? 'مركز المساعدة' : 'Help center'}</span></li>
          {user && (user.is_staff || user.is_superuser) && (
            <li onClick={() => goAndClose('/moderation')}><FlagOutlined /> <span>{tx.moderation}</span></li>
          )}

          {/* ── Categories (single link, opens dedicated page) ── */}
          <li onClick={() => goAndClose('/categories')}>
            <AppstoreOutlined /> <span>{tx.drawerCategories}</span>
          </li>

          <li className="drawer-section-label">{tx.footerLegal}</li>
          <li onClick={() => goAndClose('/legal/rights')}><SafetyOutlined /> <span>{tx.fRights}</span></li>
          <li onClick={() => goAndClose('/legal/terms')}><FileProtectOutlined /> <span>{tx.fTerms}</span></li>
          <li onClick={() => goAndClose('/legal/privacy')}><LockOutlined /> <span>{tx.fPrivacy}</span></li>

          {user ? (
            <>
              <li className="drawer-divider"></li>
              <li onClick={() => goAndClose('/profile')}><UserOutlined /> <span>{tx.profile}</span></li>
              {user && <li onClick={() => goAndClose('/dashboard')}><DashboardOutlined /> <span>{tx.dashboard}</span></li>}
              {user && <li onClick={() => goAndClose('/favorites')}><HeartOutlined /> <span>{tx.favorites}</span></li>}
              {user && <li onClick={() => goAndClose('/saved-searches')}><BellFilled /> <span>{tx.savedSearches}</span></li>}
              {user && <li onClick={() => goAndClose('/refer')}><GiftOutlined /> <span>{tx.refer}</span></li>}
              <li onClick={() => { logout(); closeDrawer(); }} className="drawer-logout"><LogoutOutlined /> <span>{tx.logout}</span></li>
            </>
          ) : (
            <>
              <li className="drawer-divider"></li>
              <li onClick={() => goAndClose('/login')}><RightOutlined /> <span>{tx.login}</span></li>
              <li onClick={() => goAndClose('/register')} className="drawer-cta"><span style={{display:"inline-flex",alignItems:"center",justifyContent:"center",width:24,height:24,borderRadius:"50%",background:"#F39C12",color:"white",marginInlineEnd:6,boxShadow:"0 2px 4px rgba(243,156,18,.3)"}}><PlusOutlined style={{fontSize:13,fontWeight:900}} /></span> <span>{tx.register}</span></li>
            </>
          )}
        </ul>
      </Drawer>
    </Layout>
    </div>
  );
};

export default AppLayout;
